import React, { Fragment, useContext } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { getLanguagePrefix } from '@helpers/languages';
import { BackLink } from '@components/BackLink/BackLink';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import PaymentHistoryData from '@views/Payments/PaymentHistoryData';
import { DesktopOrderSummary } from '@views/Payments/DesktopOrderSummary';
import { CarImage } from '@views/Payments/CarImage';
import { OrderDetailsContext } from '@hooks/useOrderDetails';
import { ViewOrderButtonMobile } from '@views/Payments/ViewOrderButton';
import { getLocalizedContent as l } from '@helpers/languages';
import content from './PaymentPage.json';
import SEO from '@common/components/SEO/SEO';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'SchnyderSLight',
  },
  mobileDivider: {
    margin: '10px 0',
    backgroundColor: '#1B1B1B',
  },
  desktopDivider: {
    backgroundColor: '#1B1B1B',
  },
  container: {
    // marginTop: '84px',
    display: 'flex',
    padding: '136px 90px 60px 90px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '48px',
    flex: '1 0 0',
    '@media screen and (max-width: 700px)': {
      padding: '136px 0px 0px 0px',
    },
  },
  wrapper: {
    height: '100vh',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '3fr 1fr',
  },
  marginTop10: {
    marginTop: '10px',
  },
}));

const orderSelector =
  orderId =>
  // eslint-disable-next-line react/display-name
  ({
    userProfile: {
      user: { orders },
    },
  }) =>
    orders.length !== 0
      ? orders.find(order => order.order.orderId === orderId)
      : null;

const PaymentPage = ({
  location,
  topDescription,
  bottomContent,
  isLoading,
  orderId,
  hasValidOrderReceipt,
}) => {
  const { prefixLanguage } = useSelector(({ appState }) => appState);
  const language = prefixLanguage || 'en';
  const order = useSelector(orderSelector(orderId));
  const { total } = useContext(OrderDetailsContext);

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const hideOrderSummary = useMediaQuery(theme.breakpoints.down(1024));
  const achPaymentLimit = process.env.REACT_APP_ACH_PAYMENT_LIMIT || 15000;
  const cardPaymentLimit =
    language === 'en'
      ? process.env.REACT_APP_CARD_PAYMENT_LIMIT_US || 15000
      : process.env.REACT_APP_CARD_PAYMENT_LIMIT || 8000;

  const onBackClick = () =>
    window.location.assign(
      `${window.location.origin}${getLanguagePrefix(language)}/portal/designs`
    );

  return (
    <Fragment>
      <SEO
        title={l(content.title, language)}
        description={l(content.description, language)}
        location={location}
      />
      <motion.div
        key="payments-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ delay: 0.25, duration: 0.25 }}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <Box className={classes.wrapper}>
            <Container maxWidth="md">
              <Grid container spacing={3} className={classes.container}>
                <Grid item xs={12}>
                  <BackLink onClick={onBackClick} />
                </Grid>

                <Grid item xs={12}>
                  {topDescription}
                </Grid>

                {hideOrderSummary && order && (
                  <Grid item xs={12} md={10}>
                    <CarImage shortcode={order.shortcode} />
                  </Grid>
                )}

                <Grid item xs={12} md={10}>
                  <PaymentHistoryData />
                </Grid>

                {hideOrderSummary && hasValidOrderReceipt && (
                  <Grid
                    item
                    xs={12}
                    md={10}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <ViewOrderButtonMobile orderId={orderId} />
                  </Grid>
                )}

                <Grid item xs={12} md={10} style={{ width: '100%' }}>
                  <Divider
                    className={
                      isMobile ? classes.mobileDivider : classes.desktopDivider
                    }
                  />
                </Grid>

                <Grid item xs={12} md={10}>
                  {bottomContent}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={10}
                  style={{ marginBottom: isMobile ? '120px' : 0 }}
                >
                  {language === 'en' && (
                    <Typography variant="body2">
                      {l(content.cardNote, language).replace(
                        `0`,
                        cardPaymentLimit
                      )}
                    </Typography>
                  )}
                  <Typography variant="body2" className={classes.marginTop10}>
                    {l(content.achNote, language)
                      .replace(`0`, achPaymentLimit)
                      .replace('USD', total?.currency || 'USD')}
                  </Typography>
                  <Typography variant="body2" className={classes.marginTop10}>
                    {l(content.contact, language)}
                  </Typography>
                </Grid>
              </Grid>
            </Container>
            {!hideOrderSummary && order && (
              <DesktopOrderSummary
                order={order}
                total={total}
                language={language}
                hasValidOrderReceipt={hasValidOrderReceipt}
              />
            )}
          </Box>
        )}
      </motion.div>
    </Fragment>
  );
};

export default PaymentPage;
